import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import da from "dayjs/locale/da";
import { useNavigate } from "react-router-dom";
import { useAllMemberRoles } from "../api/members-api";
import { useLogeMembers } from "../api/user-api";
import { AdminHeader, Card, RoleBadge } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { Skeleton } from "../components/dss/Skeleton";

dayjs.extend(relativeTime);
dayjs.locale(da);

const MembersRoles = () => {
  const { data: members = [], isLoading } = useLogeMembers();
  const { data: roleRows = [] } = useAllMemberRoles();
  const navigate = useNavigate();

  const animateIn = useEnterAnimation(isLoading);

  const claimedCount = members.filter((m) => m.auth_user_id).length;

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app">
      <AdminHeader
        back="Admin"
        onBack={() => navigate("/edit")}
        title="Medlemmer"
        sub={isLoading ? undefined : `${members.length} I LOGEN · ${claimedCount} HAR ADGANG`}
        action={
          <button
            onClick={() => navigate("/admin/members/add")}
            className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-bilberry font-semibold flex items-center gap-1.5"
          >
            <Icon name="user-plus" size={12} />
            <span>Tilføj</span>
          </button>
        }
      />
      <div className="px-7 pt-5 pb-8">
        <Card>
          {isLoading &&
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-3 px-3.5 border-t border-line-soft first:border-t-0"
              >
                <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                <div className="flex-1 flex flex-col gap-1.5">
                  <Skeleton className="w-1/3 h-4" />
                  <Skeleton className="w-16 h-4 rounded-full" />
                </div>
              </div>
            ))}
          {!isLoading && (
            <Stagger enabled={animateIn}>
              {members.map((m) => {
                const roles = roleRows.filter((r) => r.loge_member_id === m.id).map((r) => r.role);
                const active = !!m.auth_user_id;
                // The row divider lives on the StaggerItem, not the button: the
                // button is now an only-child of its wrapper, so a `first:`
                // rule on it would match every row.
                return (
                  <StaggerItem key={m.id} className="border-t border-line-soft first:border-t-0">
                    <button
                      onClick={() => navigate(`/admin/members/${m.id}`)}
                      className={`w-full flex items-center gap-3 py-3 px-3.5 text-left ${
                        active ? "" : "opacity-50"
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-paper2 flex items-center justify-center text-base">
                        {m.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] font-bold uppercase tracking-tight">
                          {m.name}
                        </div>
                        {active && (
                          <div className="font-mono text-[10px] text-ink3 mt-0.5">
                            {m.last_active_at
                              ? `Aktiv ${dayjs(m.last_active_at).fromNow()}`
                              : "Ikke aktiv endnu"}
                          </div>
                        )}
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {roles.length === 0 && <RoleBadge role="medlem" size="text-[8.5px]" />}
                          {roles.map((r) => (
                            <RoleBadge key={r} role={r} size="text-[8.5px]" />
                          ))}
                          {!active && (
                            <span className="font-mono text-[8.5px] tracking-wide text-ink3 uppercase border border-line rounded px-1.5 py-1 inline-flex items-center gap-1">
                              <Icon name="clock" variant="regular" size={8.5} />
                              Afventer login
                            </span>
                          )}
                        </div>
                      </div>
                      <Icon name="chevron-right" size={10} className="text-ink3" />
                    </button>
                  </StaggerItem>
                );
              })}
            </Stagger>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MembersRoles;
